import React from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { getPortalDashboardModel } from '@shared/services/portalAdapters';
import { ShieldCheck, ClipboardList, CheckCircle2, Clock, AlertTriangle, Mail, BadgeCheck, ExternalLink, QrCode, MapPin, Copy, Lock, AlertTriangle as AlertTriangleIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@shared/supabaseClient';

const PortalDashboard = () => {
  const { session } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const model = getPortalDashboardModel(session);
  const [verifiedInductions, setVerifiedInductions] = React.useState([]);
  const [invites, setInvites] = React.useState([]);
  const [invitesLoading, setInvitesLoading] = React.useState(true);
  const [projects, setProjects] = React.useState([]);
  const [activePassProjectIds, setActivePassProjectIds] = React.useState(new Set());
  const [loading, setLoading] = React.useState(true);
  const [profileComplete, setProfileComplete] = React.useState(false);
  const [checkingProfile, setCheckingProfile] = React.useState(true);
  const [profileData, setProfileData] = React.useState(null);
  const [passes, setPasses] = React.useState([]);
  const [copiedPassId, setCopiedPassId] = React.useState(null);

  React.useEffect(() => {
    checkProfileCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, location]);

  React.useEffect(() => {
    if (profileComplete) {
      loadVerifiedInductions();
      loadInvites();
      loadProjects();
      loadPasses();
    } else {
      // If profile is not complete, stop loading
      setLoading(false);
      setInvitesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, profileComplete]);

  const checkProfileCompletion = async () => {
    const email = session?.person?.email;
    if (!email) {
      setCheckingProfile(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('portal_profiles')
        .select(`
          *,
          depots:attending_site_id (
            id,
            name,
            code
          )
        `)
        .eq('person_email', email)
        .maybeSingle();

      if (error) {
        throw error;
      }

      // Check if profile exists and all required fields are filled
      const isComplete = data && 
        data.person_name && 
        data.person_email && 
        data.person_phone && 
        data.person_company && 
        data.photo_url && 
        data.vehicle_reg && 
        data.attending_site_id;

      setProfileData(data);
      setProfileComplete(isComplete);
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setCheckingProfile(false);
    }
  };

  const loadVerifiedInductions = async () => {
    const email = session?.person?.email;
    if (!email) { setLoading(false); return; }
    setLoading(true);
    try {
      // Use SECURITY DEFINER RPC — bypasses RLS, case-insensitive email match,
      // returns ALL statuses so we can show the full lifecycle on the dashboard.
      const { data, error } = await supabase.rpc('portal_get_induction_summary_by_email', {
        p_email: email,
      });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      // Sort by completion date (newest first)
      const sorted = (data || []).sort((a, b) => {
        const dateA = new Date(a.approved_at || a.submitted_at || a.created_at || 0);
        const dateB = new Date(b.approved_at || b.submitted_at || b.created_at || 0);
        return dateB - dateA;
      });
      setVerifiedInductions(sorted);
    } catch (error) {
      console.error('Error loading verified inductions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvites = async () => {
    const email = session?.person?.email;
    if (!email) { setInvitesLoading(false); return; }
    setInvitesLoading(true);
    try {
      // Try new RPC first (all invites, no dedup)
      let data;
      const { data: allData, error: allErr } = await supabase.rpc('portal_get_all_invites_by_email', { p_email: email });
      if (!allErr && allData) {
        data = allData;
      } else {
        // Fallback: use summary RPC (deduped by brief) — won't show all invites but shows something
        const { data: summaryData, error: summaryErr } = await supabase.rpc('portal_get_induction_summary_by_email', { p_email: email });
        if (summaryErr) throw summaryErr;
        data = summaryData;
      }
      setInvites(data || []);
    } catch (err) {
      console.error('Error loading invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  };

  const loadProjects = async () => {
    const email = session?.person?.email;
    
    if (!email) {
      return;
    }
    
    
    try {
      // Load projects where user is the project lead
      const { data: leadProjects, error: leadError } = await supabase
        .from('projects')
        .select(`
          *,
          depots:site_id (
            id,
            name,
            code
          )
        `)
        .eq('project_lead_email', email)
        .order('created_at', { ascending: false });

      if (leadError) {
        console.error('Supabase error loading lead projects:', leadError);
        throw leadError;
      }

      // Load projects where user is a team member
      const { data: teamMemberships, error: teamError } = await supabase
        .from('project_team_members')
        .select('project_id')
        .eq('person_email', email);

      let teamProjects = [];
      if (!teamError && teamMemberships && teamMemberships.length > 0) {
        const projectIds = teamMemberships.map(m => m.project_id);
        const leadIds = new Set((leadProjects || []).map(p => p.id));
        const teamOnlyIds = projectIds.filter(id => !leadIds.has(id));

        if (teamOnlyIds.length > 0) {
          const { data: tProjects } = await supabase
            .from('projects')
            .select(`
              *,
              depots:site_id (
                id,
                name,
                code
              )
            `)
            .in('id', teamOnlyIds)
            .order('created_at', { ascending: false });
          teamProjects = (tProjects || []).map(p => ({ ...p, _isTeamMember: true }));
        }
      }
      
      const allProjects = [...(leadProjects || []), ...teamProjects];
      setProjects(allProjects);

      // ── Load active passes to determine which projects show "Badge" ──
      const { data: activePasses } = await supabase
        .from('passes')
        .select('id, project_id, depot_id')
        .ilike('person_email', email)
        .in('status', ['active', 'suspended']);

      const passSet = new Set();
      const depotPassMap = new Map(); // depot_id → true
      (activePasses || []).forEach(p => {
        if (p.project_id) passSet.add(p.project_id);
        if (p.depot_id)   depotPassMap.set(p.depot_id, true);
      });
      // Also match projects by site if pass has no project_id
      allProjects.forEach(proj => {
        if (!passSet.has(proj.id) && depotPassMap.has(proj.site_id)) {
          passSet.add(proj.id);
        }
      });
      setActivePassProjectIds(passSet);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadPasses = async () => {
    const email = session?.person?.email;
    if (!email) return;
    try {
      const { data: rawPasses } = await supabase
        .from('passes')
        .select('id, pass_token, status, valid_from, valid_until, person_name, person_company, depot_id, depots:depot_id(name)')
        .ilike('person_email', email)
        .in('status', ['active', 'suspended'])
        .order('valid_from', { ascending: false });
      if (rawPasses?.length) {
        const byDepot = new Map();
        rawPasses.forEach(p => {
          const key = p.depot_id || 'unknown';
          if (!byDepot.has(key)) byDepot.set(key, p);
        });
        setPasses(Array.from(byDepot.values()));
      }
    } catch (err) {
      console.error('Error loading passes:', err);
    }
  };

  const handleCopyPass = async (token, passId) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedPassId(passId);
      setTimeout(() => setCopiedPassId(null), 1800);
    } catch (_) {}
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'submitted':
        return <Clock className="h-4 w-4" />;
      case 'invited':
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (checkingProfile || loading) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
          Welcome, {session?.person?.name || profileData?.person_name || model.personName}
        </h1>
        <p className="text-sm text-muted-foreground">Your profile and compliance overview</p>
      </div>

      {/* Profile Photo — standalone, outside card */}
      <div className="flex flex-col items-center space-y-3">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
          <div className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl bg-slate-200 dark:bg-slate-800">
            {profileData?.photo_url ? (
              <img
                src={profileData.photo_url}
                alt={profileData.person_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-300 to-slate-400 dark:from-slate-700 dark:to-slate-800">
                <svg className="w-16 h-16 sm:w-20 sm:h-20 text-slate-500 dark:text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </div>
        <div className="text-center space-y-1">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-100 dark:bg-green-950/30 border border-green-300 dark:border-green-800">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">Profile Active</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Updated {profileData?.updated_at ? new Date(profileData.updated_at).toLocaleDateString() : 'recently'}
          </p>
        </div>
      </div>

      {/* Profile Container */}
      <Card className="border border-border shadow-sm w-full min-w-0 overflow-hidden">
        <CardHeader className="border-b border-border pb-4 px-4 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                Your Profile
              </CardTitle>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Personal and site access information
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/portal/profile')}
              className="border-amber-500/50 text-amber-600 hover:bg-amber-50 hover:border-amber-500 dark:hover:bg-amber-950/20"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Profile
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 pb-6 px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Personal Details */}
              <div className="space-y-5">
                <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Personal Details
                </h3>
                
                <div className="space-y-4">
                  <div className="">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Full Name</label>
                    <p className="text-base font-semibold text-foreground mt-0.5">{profileData?.person_name || '-'}</p>
                  </div>
                  
                  <div className="">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Email Address</label>
                    <p className="text-sm font-medium text-foreground mt-0.5 break-all">{profileData?.person_email || '-'}</p>
                  </div>
                  
                  <div className="">
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Contact Number</label>
                    <p className="text-base font-medium text-foreground mt-0.5">{profileData?.person_phone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Company & Site Details */}
              <div className="space-y-5">
                <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Company & Site
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Company Name</label>
                    <p className="text-base font-semibold text-foreground mt-0.5">{profileData?.person_company || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Site Attending</label>
                    <p className="text-base font-medium text-foreground mt-0.5">{profileData?.depots?.name || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Vehicle Registration</label>
                    <p className="text-base font-mono font-bold text-foreground mt-0.5">
                      {profileData?.vehicle_reg && profileData.vehicle_reg !== 'NO_VEHICLE' ? profileData.vehicle_reg.toUpperCase() : (profileData?.vehicle_reg === 'NO_VEHICLE' ? 'No vehicle' : '-')}
                    </p>
                  </div>
                </div>
              </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Digital Pass ─────────────────────────────────────────────────── */}
      <Card className="border border-border shadow-sm w-full min-w-0 overflow-hidden">
        <CardHeader className="border-b border-border pb-4 px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-foreground">
              <QrCode className="h-5 w-5 text-primary" />
              Your Digital Pass
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/portal/digital-badge')}
              className="border-amber-500/50 text-amber-600 hover:bg-amber-50 hover:border-amber-500 dark:hover:bg-amber-950/20"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Full View
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 pb-6 px-4 sm:px-6">
          {passes.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="w-16 h-16 rounded-full bg-muted border-2 border-border flex items-center justify-center">
                <Lock className="h-7 w-7 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">No active pass yet</p>
                <p className="text-xs text-muted-foreground mt-1">Your pass is issued automatically once your induction or project is approved.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {passes.map((pass) => {
                const isSuspended = pass.status === 'suspended';
                return (
                  <div key={pass.id} className={`rounded-xl border overflow-hidden ${isSuspended ? 'border-amber-300 bg-amber-50/40' : 'border-border bg-card'}`}>
                    <div className={`px-4 py-2.5 flex items-center justify-between border-b ${isSuspended ? 'bg-amber-100/60 border-amber-200' : 'bg-muted/40 border-border'}`}>
                      <div className="flex items-center gap-2">
                        <MapPin className={`h-4 w-4 ${isSuspended ? 'text-amber-600' : 'text-primary'}`} />
                        <span className="text-sm font-bold text-foreground">{pass.depots?.name || 'Site Badge'}</span>
                      </div>
                      {isSuspended ? (
                        <span className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5">
                          <AlertTriangle className="h-3 w-3" /> Suspended
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-100 border border-green-300 rounded-full px-2.5 py-0.5">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      )}
                    </div>
                    <div className="p-4 flex flex-col sm:flex-row items-center sm:items-start gap-5">
                      {isSuspended ? (
                        <div className="rounded-xl border-2 border-amber-300/60 bg-amber-50 p-4 flex items-center justify-center w-[120px] h-[120px] flex-shrink-0">
                          <Lock className="h-10 w-10 text-amber-400" />
                        </div>
                      ) : (
                        <div className="rounded-xl border-2 border-border bg-white p-2.5 shadow-sm flex-shrink-0 self-center sm:self-start">
                          <QRCodeSVG value={`APEX:${pass.pass_token}`} size={120} />
                        </div>
                      )}
                      <div className="flex-1 space-y-2 text-center sm:text-left">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Valid from</span><p className="font-medium">{fmt(pass.valid_from)}</p></div>
                          <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Valid until</span><p className="font-medium">{fmt(pass.valid_until)}</p></div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <code className="text-xs bg-muted px-2 py-1 rounded border border-border font-mono">{pass.pass_token}</code>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleCopyPass(pass.pass_token, pass.id)}>
                            {copiedPassId === pass.id ? <><CheckCircle2 className="h-3 w-3 mr-1" />Copied</> : <><Copy className="h-3 w-3 mr-1" />Copy</>}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">Show QR code at the site kiosk on arrival.</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verified Inductions Table */}
      <Card className="border border-border shadow-sm">
        <CardHeader className="border-b border-border bg-muted/40 py-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-foreground">
                <div className="p-1.5 bg-primary/10 rounded-lg">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                </div>
                Inductions
              </CardTitle>
              <p className="text-sm font-medium text-muted-foreground mt-0.5">
                Your induction history and current compliance status
              </p>
            </div>
            {verifiedInductions.length > 0 && (
              <Badge variant="outline" className="text-base font-semibold border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400">
                {verifiedInductions.length} Total
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4 px-3 sm:px-6 pb-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent mx-auto"></div>
              <p className="mt-3 text-sm text-muted-foreground">Loading inductions...</p>
            </div>
          ) : verifiedInductions.length === 0 ? (
            <div className="flex items-center justify-between px-4 py-5 rounded-lg border border-dashed border-border">
              <p className="text-sm text-muted-foreground">
                No inductions submitted yet. Your induction status and progress can be found on the{' '}
                <button
                  onClick={() => navigate('/portal/inductions')}
                  className="font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                >
                  Invites page
                </button>
                .
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[480px]">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Induction Brief</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Site</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Version</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Completed</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {verifiedInductions.map((induction) => {
                      const status = induction.status;
                      const statusConfig = {
                        approved:                { color: 'bg-green-500',  label: 'Completed' },
                        submitted:               { color: 'bg-amber-500',  label: 'Awaiting Review' },
                        in_progress:             { color: 'bg-sky-500',    label: 'In Progress' },
                        pending_acknowledgement: { color: 'bg-blue-500',   label: 'Pending' },
                        invited:                 { color: 'bg-slate-400',  label: 'Not Started' },
                      }[status] || { color: 'bg-slate-400', label: status };

                      // Use approved_at for completed, otherwise submitted_at
                      const completedDate = induction.approved_at || induction.submitted_at;

                      return (
                        <tr key={induction.submission_id} className="hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-4">
                            <p className="text-sm font-semibold text-foreground">
                              {induction.brief_title || 'Unknown Brief'}
                            </p>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-foreground whitespace-nowrap">
                            {induction.depot_name || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-3 px-4 text-xs font-medium text-foreground whitespace-nowrap">
                            {induction.brief_version || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-3 px-4 text-xs font-medium text-foreground whitespace-nowrap">
                            {completedDate
                              ? new Date(completedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${statusConfig.color} text-white text-xs flex items-center gap-1 w-fit px-2 py-0.5`}>
                              {getStatusIcon(status)}
                              <span className="font-semibold">{statusConfig.label}</span>
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invites Table */}
      <Card className="border border-border shadow-sm">
        <CardHeader className="border-b border-border bg-muted/40 py-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-foreground">
                <div className="p-1.5 bg-primary/10 rounded-lg">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                Invites
              </CardTitle>
              <p className="text-sm font-medium text-muted-foreground mt-0.5">
                Your site visit invitations and digital badges
              </p>
            </div>
            {invites.length > 0 && (
              <Badge variant="outline" className="text-base font-semibold border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400">
                {invites.length} Total
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4 px-3 sm:px-6 pb-4">
          {invitesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent mx-auto"></div>
              <p className="mt-3 text-sm text-muted-foreground">Loading invites...</p>
            </div>
          ) : invites.length === 0 ? (
            <div className="flex items-center justify-between px-4 py-5 rounded-lg border border-dashed border-border">
              <p className="text-sm text-muted-foreground">
                No invites yet. The site team will send you an invitation when you're invited to visit a site.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Induction Brief</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Site</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Date</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Badge</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invites.map((invite) => {
                      const status = invite.status;
                      const inviteStatusConfig = {
                        approved:                { color: 'bg-green-500',  label: 'Approved' },
                        submitted:               { color: 'bg-amber-500',  label: 'Submitted' },
                        in_progress:             { color: 'bg-sky-500',    label: 'In Progress' },
                        pending_acknowledgement: { color: 'bg-blue-500',   label: 'Re-confirm' },
                        invited:                 { color: 'bg-slate-400',  label: 'Not Started' },
                      }[status] || { color: 'bg-slate-400', label: status };

                      const inviteDate = invite.valid_from || invite.created_at;

                      return (
                        <tr
                          key={invite.submission_id}
                          className="hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/portal/inductions/${invite.submission_id}`)}
                        >
                          <td className="py-3 px-4">
                            <p className="text-sm font-semibold text-foreground">
                              {invite.brief_title || invite.title || 'Site Visit'}
                            </p>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-foreground whitespace-nowrap">
                            {invite.depot_name || <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-3 px-4 text-xs font-medium text-foreground whitespace-nowrap">
                            {inviteDate
                              ? new Date(inviteDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${inviteStatusConfig.color} text-white text-xs flex items-center gap-1 w-fit px-2 py-0.5`}>
                              {getStatusIcon(status)}
                              <span className="font-semibold">{inviteStatusConfig.label}</span>
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {status === 'approved' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 px-2"
                                onClick={(e) => { e.stopPropagation(); navigate(`/portal/digital-badge?submission=${invite.submission_id}`); }}
                              >
                                <BadgeCheck className="h-3.5 w-3.5 mr-1" />
                                View
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projects Section */}
      <Card className="border border-border shadow-sm">
        <CardHeader className="border-b border-border bg-muted/40 py-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-bold text-foreground">
                <div className="p-1.5 bg-primary/10 rounded-lg">
                  <ClipboardList className="h-4 w-4 text-primary" />
                </div>
                Your Projects
              </CardTitle>
              <p className="text-sm font-medium text-muted-foreground mt-0.5">
                Active and completed project assignments
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 px-3 sm:px-6 pb-4">
          {projects.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <ClipboardList className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                No Projects Yet
              </h3>
              <p className="text-sm font-medium text-muted-foreground max-w-md mx-auto">
                You don't have any assigned projects at the moment. Check back later or contact your site administrator.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="bg-muted/60 border-b border-border">
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Project</th>
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Site</th>
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Role</th>
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Dates</th>
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">Status</th>
                    <th className="py-3 px-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {projects.map((project) => {
                    const statusMap = {
                      draft_pending_supplier: { text: 'Awaiting Setup', color: 'bg-blue-500' },
                      draft_in_progress: { text: 'In Progress', color: 'bg-yellow-500' },
                      submitted_awaiting_approval: { text: 'Pending Approval', color: 'bg-orange-500' },
                      approved: { text: 'Approved', color: 'bg-green-500' },
                      rejected: { text: 'Rejected', color: 'bg-red-500' },
                      active: { text: 'Active', color: 'bg-emerald-500' },
                      completed: { text: 'Completed', color: 'bg-gray-500' },
                      archived: { text: 'Archived', color: 'bg-gray-400' },
                    };
                    const { text: statusText, color: statusColor } = statusMap[project.status] || { text: project.status, color: 'bg-gray-500' };
                    const hasActiveBadge = activePassProjectIds.has(project.id);

                    return (
                      <tr
                        key={project.id}
                        className="hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => navigate(
                          hasActiveBadge
                            ? `/portal/digital-badge?project=${project.id}`
                            : `/portal/projects/${project.id}`
                        )}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {project.project_name}
                            </p>
                            {project.project_summary && (
                              <p className="text-xs font-medium text-muted-foreground mt-0.5 line-clamp-1">
                                {project.project_summary}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-foreground whitespace-nowrap">
                          {project.depots?.name || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${project._isTeamMember ? 'bg-blue-500' : 'bg-indigo-500'} text-white text-xs w-fit px-2 py-0.5`}>
                            {project._isTeamMember ? 'Team Member' : 'Lead'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-xs font-medium text-foreground whitespace-nowrap">
                          {new Date(project.estimated_start_date).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: 'short' 
                          })}
                          {' – '}
                          {new Date(project.estimated_end_date).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${statusColor} text-white text-xs flex items-center gap-1 w-fit px-2 py-0.5`}>
                            {getStatusIcon(project.status)}
                            <span className="font-semibold">{statusText}</span>
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                hasActiveBadge
                                  ? `/portal/digital-badge?project=${project.id}`
                                  : `/portal/projects/${project.id}`
                              );
                            }}
                          >
                            {hasActiveBadge
                              ? <><BadgeCheck className="h-3.5 w-3.5 mr-1" />Badge</>
                              : <><ExternalLink className="h-3.5 w-3.5 mr-1" />View</>
                            }
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalDashboard;
